import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  Typography,
  Avatar,
  Box,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Divider,
} from "@mui/material";
import {
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from "@mui/icons-material";
import { ADMIN_ROUTES } from "../../routes/Admin/AdminPaths";

const UserDetailModal = ({ open, user, onClose, onSave, loading }) => {
  const history = useHistory();
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (user) {
      setEditData({
        username: user.username || "",
        email: user.email || "",
        account_type: user.account_type || "basic",
        available_coins: user.available_coins || 0,
        available_xp: user.available_xp || 0,
      });
    }
  }, [user]);

  const handleEditToggle = () => {
    setEditMode(!editMode);
    setFormErrors({});
    if (editMode) {
      setEditData({
        username: user.username || "",
        email: user.email || "",
        account_type: user.account_type || "basic",
        available_coins: user.available_coins || 0,
        available_xp: user.available_xp || 0,
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditData((prev) => ({
      ...prev,
      [field]: value,
    }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!editData.username?.trim()) {
      errors.username = "Username is required";
    } else if (editData.username.length < 3) {
      errors.username = "Username must be at least 3 characters";
    }

    if (!editData.email?.trim()) {
      errors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(editData.email)) {
      errors.email = "Email is invalid";
    }

    if (editData.available_coins < 0) {
      errors.available_coins = "Coins cannot be negative";
    }

    if (editData.available_xp < 0) {
      errors.available_xp = "XP cannot be negative";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(user.id, editData);
      setEditMode(false);
    }
  };

  const handleClose = () => {
    setEditMode(false);
    setFormErrors({});
    onClose();
  };

  const handleFullPreview = () => {
    handleClose();
    history.push(ADMIN_ROUTES.userDetail.replace(":id", user.id));
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  const getAccountTypeColor = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "basic":
        return "default";
      case "standard":
        return "primary";
      case "premium":
        return "secondary";
      case "elite":
        return "warning";
      default:
        return "default";
    }
  };

  const getAccountTypeIcon = (accountType) => {
    switch (accountType?.toLowerCase()) {
      case "elite":
        return "👑 ";
      case "premium":
        return "💎 ";
      case "standard":
        return "⭐ ";
      default:
        return "";
    }
  };

  if (!user) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Quick User Info
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ py: 2 }}>
          {/* User Avatar and Basic Info */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
              p: 2.5,
              borderRadius: 2,
              bgcolor: "grey.50",
              border: "1px solid",
              borderColor: "grey.200",
            }}
          >
            <Avatar
              src={user.profile_image_url}
              sx={{
                width: 72,
                height: 72,
                border: "3px solid",
                borderColor: "background.paper",
                boxShadow: 2,
              }}
            >
              {user.username?.charAt(0)?.toUpperCase() || "U"}
            </Avatar>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                {user.username || "No username"}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                {user.email}
              </Typography>
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  label={`${getAccountTypeIcon(user.account_type)}${user.account_type?.toUpperCase() || "BASIC"}`}
                  size="small"
                  color={getAccountTypeColor(user.account_type)}
                />
                {!!user.is_verified && (
                  <Chip label="Verified" size="small" color="success" />
                )}
                {user.is_banned ? (
                  <Chip label="Banned" size="small" color="error" />
                ) : (
                  <Chip label="Active" size="small" color="success" />
                )}
                <Chip
                  label={user.is_online ? "Online" : "Offline"}
                  size="small"
                  color={user.is_online ? "success" : "default"}
                />
              </Box>
            </Box>
          </Box>

          {/* Editable Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Username"
                value={editMode ? editData.username : user.username || "N/A"}
                onChange={(e) => handleInputChange("username", e.target.value)}
                disabled={!editMode}
                error={!!formErrors.username}
                helperText={formErrors.username}
                size="small"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={editMode ? editData.email : user.email || "N/A"}
                onChange={(e) => handleInputChange("email", e.target.value)}
                disabled={!editMode}
                error={!!formErrors.email}
                helperText={formErrors.email}
                size="small"
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Coins"
                type="number"
                value={editMode ? editData.available_coins : user.available_coins || 0}
                onChange={(e) =>
                  handleInputChange("available_coins", parseInt(e.target.value) || 0)
                }
                disabled={!editMode}
                error={!!formErrors.available_coins}
                helperText={formErrors.available_coins}
                size="small"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={6}>
              <TextField
                fullWidth
                label="XP"
                type="number"
                value={editMode ? editData.available_xp : user.available_xp || 0}
                onChange={(e) =>
                  handleInputChange("available_xp", parseInt(e.target.value) || 0)
                }
                disabled={!editMode}
                error={!!formErrors.available_xp}
                helperText={formErrors.available_xp}
                size="small"
                inputProps={{ min: 0 }}
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth disabled={!editMode} size="small">
                <InputLabel>Account Type</InputLabel>
                <Select
                  value={editMode ? editData.account_type : user.account_type || "basic"}
                  label="Account Type"
                  onChange={(e) => handleInputChange("account_type", e.target.value)}
                >
                  <MenuItem value="basic">Basic</MenuItem>
                  <MenuItem value="standard">Standard</MenuItem>
                  <MenuItem value="premium">Premium</MenuItem>
                  <MenuItem value="elite">Elite</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Basic Read-only Info */}
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Joined
              </Typography>
              <Typography variant="body2">
                {formatDate(user.created_at)}
              </Typography>
            </Grid>

            <Grid item xs={6}>
              <Typography variant="caption" color="text.secondary">
                Last Seen
              </Typography>
              <Typography variant="body2">
                {formatDate(user.last_seen)}
              </Typography>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {editMode ? (
          <>
            <Button onClick={handleEditToggle} color="inherit">
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={loading}
            >
              Save
            </Button>
          </>
        ) : (
          <>
            <Button
              onClick={handleFullPreview}
              startIcon={<VisibilityIcon />}
              variant="outlined"
              fullWidth
            >
              View Full Profile
            </Button>
            <Button
              onClick={handleEditToggle}
              startIcon={<EditIcon />}
              variant="contained"
            >
              Edit
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default UserDetailModal;
